import { isE164, normalizePhoneInput } from "../../common/phone";
import { Filter } from "../users/UserFilters";

/**
 * The pure half of the Send WhatsApp screen. Backend reference:
 * docs/WHATSAPP_BROADCAST.md in ilbp-backend.
 */

export interface WhatsAppTemplate {
  sid: string;
  name: string;
  language: string;
  body: string;
  variables: string[];
  category: string | null;
  status: string;
  rejectionReason: string | null;
}

export interface WhatsAppBroadcastRow {
  id: string;
  templateName: string;
  status: "QUEUED" | "RUNNING" | "COMPLETED" | "FAILED" | "CANCELLED";
  totalRecipients: number;
  sent: number;
  skipped: number;
  failed: number;
  dryRun: boolean;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

/** What `{firstName}` shows as in the preview. The server fills each member's own. */
export const PREVIEW_FIRST_NAME = "Ada";

export const MAX_TEST_PHONES = 5;

/** The same mapping as Users.tsx and SmsWithFilters.tsx, so one filter selects the same people everywhere. */
export function toUserFilterInput(filter: Filter) {
  return {
    fieldFilter: {
      filters: filter.filters,
      operator: filter.operator,
    },
    hasReferred: filter.usersThatHaveReferred,
    hasBeenReferred: filter.usersThatHaveBeenReferred,
    hasRecommendedABusiness: filter.usersThatHaveRecommended,
    hasRecommendedSameBusiness: filter.usersThatHaveRecommendedSameBusiness,
    visitedPage: filter.visitedPage,
    hasSharedReferralLink: filter.hasSharedReferralLink,
    referredByUser: filter.referredByUser,
  };
}

export const isApproved = (template: WhatsAppTemplate) => template.status === "approved";

export const isFinished = (status: WhatsAppBroadcastRow["status"]) =>
  status === "COMPLETED" || status === "FAILED" || status === "CANCELLED";

/** Placeholders still without a value. Send and test stay disabled until this is empty. */
export function missingVariables(template: WhatsAppTemplate | undefined, values: Record<string, string>): string[] {
  if (!template) return [];
  return template.variables.filter((key) => !(values[key] ?? "").trim());
}

/** The variables argument both mutations take. */
export function toVariablesInput(template: WhatsAppTemplate, values: Record<string, string>) {
  return template.variables.map((key) => ({ key, value: (values[key] ?? "").trim() }));
}

/** The body as a member named `firstName` would read it. Empty placeholders stay visible. */
export function previewBody(template: WhatsAppTemplate, values: Record<string, string>, firstName = PREVIEW_FIRST_NAME): string {
  return template.body.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (whole, key: string) => {
    const value = (values[key] ?? "").trim();
    return value ? value.replace(/\{firstName\}/g, firstName) : whole;
  });
}

/** Numbers typed for a test, split on commas, spaces or new lines. */
export function parsePhones(text: string): { phones: string[]; invalid: string[] } {
  const items = text
    .split(/[,;\n]+/)
    .map(normalizePhoneInput)
    .filter(Boolean);
  const phones = Array.from(new Set(items));
  return { phones, invalid: phones.filter((phone) => !isE164(phone)) };
}

export function describeProgress(row: Pick<WhatsAppBroadcastRow, "totalRecipients" | "sent" | "skipped" | "failed" | "dryRun" | "status">): string {
  if (row.dryRun && row.status === "COMPLETED") {
    return `Dry run: would have reached ${row.totalRecipients} member${row.totalRecipients === 1 ? "" : "s"}. Nothing was sent.`;
  }
  const parts = [`${row.sent} of ${row.totalRecipients} sent`];
  if (row.skipped) parts.push(`${row.skipped} skipped (messaged too often recently)`);
  if (row.failed) parts.push(`${row.failed} failed`);
  return parts.join(" · ");
}

/** A fresh key per composition: a retried click reuses it, a new message gets a new one. */
export function newIdempotencyKey(): string {
  const uuid = globalThis.crypto?.randomUUID;
  if (typeof uuid === "function") return globalThis.crypto.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}`;
}
